import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { ReactComponent as ShoppingBag } from "../../../icons/cart icon 1.svg";

export default function EmptyCart() {
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    const sequence = async () => {
      await controls.start("visible");
      // Continuous floating animation
      controls.start({
        y: [0, -8, 0],
        transition: {
          repeat: Infinity,
          repeatType: "reverse",
          duration: 2.5,
          ease: "easeInOut"
        }
      });
    };
    sequence();
  }, [controls]);

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const item = {
    hidden: { y: 15, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100
      }
    }
  };

  return (
    <motion.div
      className="flex flex-col items-center justify-center h-[60vh] text-center px-2"
      variants={container}
      initial="hidden"
      animate={controls}
    >
      <div className="relative mb-8">
        <motion.div
          className="relative"
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            repeat: Infinity,
            repeatType: "reverse",
            duration: 2,
            ease: "easeInOut"
          }}
        >
          <ShoppingBag
            size={80}
            className="text-primary z-10 relative"
            fill="#FF7236"
            stroke="none"
          />
          <div className="absolute inset-0 bg-[#FF7236] rounded-full opacity-10 blur-lg scale-110" />
        </motion.div>

        {/* Floating book elements */}
        <AnimatePresence>
          {[1, 2, 3].map((i) => (
            <motion.div
              key={i}
              className="absolute bg-primary rounded-sm opacity-20"
              style={{
                width: 16,
                height: 22,
                top: `${15 + Math.sin(i) * 20}%`,
                left: `${20 + (i-1) * 30}%`,
              }}
              animate={{
                y: [0, -15, 0],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 2 + i,
                repeat: Infinity,
                repeatType: "reverse",
                delay: i * 0.3,
                ease: "easeInOut"
              }}
            />
          ))}
        </AnimatePresence>
      </div>

      <motion.h2
        variants={item}
        className="text-3xl font-bold text-text-black mb-3"
      >
        Your cart is <span className="inline-block">empty <motion.span
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="inline-block"
        ></motion.span></span>
      </motion.h2>
      <motion.p
        variants={item}
        className="text-lg text-gray-600 max-w-md mb-6"
      >
        Start adding books to buy! Don't miss out on your next favorite read.
      </motion.p>

      <motion.button
        variants={item}
        onClick={() => navigate("/explore-books")}
        className="mt-2 px-8 py-3.5 bg-white border-2 border-primary rounded-full text-primary font-medium relative overflow-hidden group"
        whileHover={{
          scale: 1.05,
          backgroundColor: "#FF7236",
          color: "#fff",
          boxShadow: "0 10px 25px rgba(255, 114, 54, 0.3)"
        }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <span className="relative z-10 flex items-center gap-2">
          <motion.span
            animate={{ x: [0, 4, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
          </motion.span>
          Explore Books
        </span>
        <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity rounded-full" />
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-25 w-[60%] -rotate-[30deg] group-hover:animate-shine" />
        {/* Button confetti effect */}
        <AnimatePresence>
          <motion.span
            className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-[#FF7236]"
            animate={{
              y: [0, -20, 0],
              x: [0, 10, 0],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 2
            }}
          />
          <motion.span
            className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#FF7236]"
            animate={{
              y: [0, -15, 0],
              x: [0, -10, 0],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 2.5
            }}
          />
        </AnimatePresence>
      </motion.button>

      {/* Background floating elements */}
      <AnimatePresence>
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full bg-primary opacity-10"
            style={{
              top: `${10 + Math.random() * 80}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0, -10, 0],
              x: [0, 10, 0, -10, 0],
            }}
            transition={{
              duration: 15 + Math.random() * 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </AnimatePresence>
    </motion.div>
  );
}